import React, { useRef, useCallback } from 'react';
import { useMutation } from '@apollo/client';
import { MUTATION } from '../gql-operations';
import { AuthContext } from '../util/context';
import { isFileTypeImage } from '../constants/fileUtils';

// Multipart upload configuration
export const MULTIPART_CONFIG = {
    THRESHOLD: 100 * 1024 * 1024,    // 100MB - use multipart above this
    CHUNK_SIZE: 50 * 1024 * 1024,    // 50MB per part
    MAX_CONCURRENT: 4                 // parallel uploads
};

/**
 * Hook for handling multipart uploads to S3
 * Used for files larger than MULTIPART_CONFIG.THRESHOLD (100MB)
 */
function useMultipartUpload() {
    const abortControllerRef = useRef(null);
    const uploadStateRef = useRef(null);
    const uploadProgressRef = useRef({});

    const {
        state: { uploadProgress },
        context: { setUploadProgress }
    } = React.useContext(AuthContext);

    // Keep uploadProgress ref in sync with state (avoids stale closures)
    React.useEffect(() => {
        uploadProgressRef.current = uploadProgress || {};
    }, [uploadProgress]);

    const [initiateMultipartUpload] = useMutation(MUTATION.INITIATE_MULTIPART_UPLOAD);
    const [completeMultipartUpload] = useMutation(MUTATION.COMPLETE_MULTIPART_UPLOAD);
    const [abortMultipartUpload] = useMutation(MUTATION.ABORT_MULTIPART_UPLOAD);

    /**
     * Splits a file into chunks
     */
    const getFileChunks = (file, chunkSize = MULTIPART_CONFIG.CHUNK_SIZE) => {
        const chunks = [];
        let start = 0;
        let partNumber = 1;

        while (start < file.size) {
            const end = Math.min(start + chunkSize, file.size);
            chunks.push({
                partNumber,
                blob: file.slice(start, end),
                start,
                end
            });
            start = end;
            partNumber++;
        }

        return chunks;
    };

    /**
     * Uploads a single part to S3
     */
    const uploadPart = (signedUrl, chunk, onProgress) => {
        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();

            const cleanupXhr = () => {
                if (uploadStateRef.current?.xhrs) {
                    const idx = uploadStateRef.current.xhrs.indexOf(xhr);
                    if (idx !== -1) {
                        uploadStateRef.current.xhrs.splice(idx, 1);
                    }
                }
            };

            xhr.upload.addEventListener('progress', (event) => {
                if (uploadStateRef.current?.aborted) return;
                if (event.lengthComputable && onProgress) {
                    onProgress(chunk.partNumber, event.loaded, event.total);
                }
            });

            xhr.addEventListener('load', () => {
                cleanupXhr();
                if (xhr.status >= 200 && xhr.status < 300) {
                    // Get ETag from response headers (required for completing multipart upload)
                    const eTag = xhr.getResponseHeader('ETag');
                    resolve({
                        partNumber: chunk.partNumber,
                        eTag: eTag ? eTag.replace(/"/g, '') : null
                    });
                } else {
                    reject(new Error(`Upload failed with status ${xhr.status}`));
                }
            });

            xhr.addEventListener('error', () => {
                cleanupXhr();
                reject(new Error('Network error during upload'));
            });

            xhr.addEventListener('abort', () => {
                cleanupXhr();
                reject(new Error('Upload cancelled'));
            });

            // Store xhr for potential abort
            if (uploadStateRef.current) {
                if (uploadStateRef.current.aborted) {
                    reject(new Error('Upload cancelled'));
                    return;
                }
                uploadStateRef.current.xhrs.push(xhr);
            }

            xhr.open('PUT', signedUrl);
            xhr.send(chunk.blob);
        });
    };

    /**
     * Uploads chunks with limited concurrency
     */
    const uploadChunksWithConcurrency = async (parts, chunks, onProgress, maxConcurrent = MULTIPART_CONFIG.MAX_CONCURRENT) => {
        const completedParts = [];
        const partProgress = {};
        let currentIndex = 0;

        // Initialize progress tracking
        chunks.forEach(chunk => {
            partProgress[chunk.partNumber] = { loaded: 0, total: chunk.blob.size };
        });

        const calculateTotalProgress = () => {
            let totalLoaded = 0;
            let totalSize = 0;
            Object.values(partProgress).forEach(p => {
                totalLoaded += p.loaded;
                totalSize += p.total;
            });
            return totalSize > 0 ? (totalLoaded / totalSize) * 100 : 0;
        };

        const uploadNext = async () => {
            if (currentIndex >= chunks.length) return null;
            if (uploadStateRef.current?.aborted) return null;

            const chunk = chunks[currentIndex];
            const part = parts.find(p => p.partNumber === chunk.partNumber);
            currentIndex++;

            if (!part) {
                throw new Error(`No signed URL for part ${chunk.partNumber}`);
            }

            const result = await uploadPart(part.signedUrl, chunk, (partNum, loaded, total) => {
                if (uploadStateRef.current?.aborted) return;
                partProgress[partNum] = { loaded, total };
                const percentage = calculateTotalProgress();
                onProgress(percentage);
            });

            if (uploadStateRef.current?.aborted) return null;

            completedParts.push(result);

            // Process next chunk if available
            return uploadNext();
        };

        // Start concurrent uploads
        const workers = [];
        for (let i = 0; i < Math.min(maxConcurrent, chunks.length); i++) {
            workers.push(uploadNext());
        }

        await Promise.all(workers);

        // Sort by part number (required for S3 completion)
        return completedParts.sort((a, b) => a.partNumber - b.partNumber);
    };

    /**
     * Main multipart upload function
     * @param {File} file - The file to upload
     * @param {string} fileName - The file name for S3
     * @param {Object} message - Optional message object for progress key
     * @param {Function} onProgress - Optional callback for progress updates (0-100)
     * @returns {Promise<{url: string, key: string}>}
     */
    const uploadMultipart = useCallback(async (file, fileName, message = null, onProgress = null, options = {}) => {
        const { signal } = options;
        const progressKey = options.progressKey || (message
            ? `${message.id}_${isFileTypeImage(file.type) ? 'image' : 'file'}`
            : file.name);

        // Initialize upload state
        uploadStateRef.current = {
            key: null,
            uploadId: null,
            xhrs: [],
            aborted: Boolean(signal?.aborted)
        };

        const clearMultipartProgress = () => {
            const current = uploadProgressRef.current || {};
            const rest = {};
            Object.entries(current).forEach(([k, v]) => {
                if (k !== progressKey) {
                    rest[k] = v;
                }
            });
            uploadProgressRef.current = rest;
            setUploadProgress(Object.keys(rest).length > 0 ? rest : undefined);
        };

        const onAbort = () => {
            if (uploadStateRef.current) {
                uploadStateRef.current.aborted = true;
                uploadStateRef.current.xhrs.forEach(xhr => {
                    try {
                        xhr.abort();
                    } catch (e) {}
                });
            }
            clearMultipartProgress();
        };

        if (signal) {
            signal.addEventListener('abort', onAbort, { once: true });
        }

        try {
            if (signal?.aborted) {
                clearMultipartProgress();
                throw new Error('Upload cancelled');
            }

            // Split file into chunks
            const chunks = getFileChunks(file);

            // Update progress to show we're starting
            const initialProgress = {
                ...uploadProgressRef.current,
                [progressKey]: {
                    state: 'pending',
                    percentage: 0,
                    type: isFileTypeImage(file.type) ? 'image' : 'file',
                    size: file.size
                }
            };
            uploadProgressRef.current = initialProgress;
            setUploadProgress(initialProgress);

            // Initiate multipart upload
            // Server will generate UUID-based path (stored-files/{uuid}.{ext})
            const initiateResponse = await initiateMultipartUpload({
                variables: {
                    data: {
                        fileName: fileName,  // Original filename - server will generate UUID path
                        fileType: file.type,
                        fileSize: file.size,
                        partCount: chunks.length
                    }
                }
            });

            const { uploadId, key, parts } = initiateResponse.data.initiateMultipartUpload;

            // Store for potential abort
            uploadStateRef.current.key = key;
            uploadStateRef.current.uploadId = uploadId;

            // Check if aborted before starting uploads
            if (uploadStateRef.current.aborted || signal?.aborted) {
                clearMultipartProgress();
                throw new Error('Upload cancelled');
            }

            // Upload all chunks with progress tracking
            const completedParts = await uploadChunksWithConcurrency(
                parts,
                chunks,
                (percentage) => {
                    if (uploadStateRef.current?.aborted || signal?.aborted) return;
                    const currentProgress = uploadProgressRef.current || {};
                    const updatedProgress = {
                        ...currentProgress,
                        [progressKey]: {
                            state: 'pending',
                            percentage,
                            type: isFileTypeImage(file.type) ? 'image' : 'file',
                            size: file.size
                        }
                    };
                    uploadProgressRef.current = updatedProgress;
                    setUploadProgress(updatedProgress);
                    // Call external progress callback if provided
                    if (onProgress) {
                        onProgress(percentage);
                    }
                }
            );

            // Check if aborted before completing
            if (uploadStateRef.current.aborted || signal?.aborted) {
                clearMultipartProgress();
                throw new Error('Upload cancelled');
            }

            // Complete multipart upload
            const completeResponse = await completeMultipartUpload({
                variables: {
                    data: {
                        key,
                        uploadId,
                        parts: completedParts
                    }
                }
            });

            // Update progress to complete
            const currentProgress = uploadProgressRef.current || {};
            const finalProgress = {
                ...currentProgress,
                [progressKey]: {
                    state: 'done',
                    percentage: 100,
                    type: isFileTypeImage(file.type) ? 'image' : 'file',
                    size: file.size
                }
            };
            uploadProgressRef.current = finalProgress;
            setUploadProgress(finalProgress);

            // Clear progress after a short delay
            setTimeout(() => {
                setUploadProgress(undefined);
            }, 500);

            return completeResponse.data.completeMultipartUpload;

        } catch (error) {
            const isCancelled = signal?.aborted || error?.message === 'Upload cancelled' || uploadStateRef.current?.aborted;
            if (isCancelled) {
                clearMultipartProgress();
            }

            // Abort the multipart upload on error or cancellation
            if (uploadStateRef.current?.key && uploadStateRef.current?.uploadId) {
                try {
                    await abortMultipartUpload({
                        variables: {
                            data: {
                                key: uploadStateRef.current.key,
                                uploadId: uploadStateRef.current.uploadId
                            }
                        }
                    });
                } catch (abortError) {
                    console.error('Error aborting multipart upload:', abortError);
                }
            }

            if (!isCancelled) {
                // Update progress to error state
                const currentProgress = uploadProgressRef.current || {};
                const errorProgress = {
                    ...currentProgress,
                    [progressKey]: {
                        state: 'error',
                        percentage: 0,
                        type: isFileTypeImage(file.type) ? 'image' : 'file',
                        size: file.size
                    }
                };
                uploadProgressRef.current = errorProgress;
                setUploadProgress(errorProgress);
            }

            throw error;
        } finally {
            if (signal) {
                signal.removeEventListener('abort', onAbort);
            }
            if (signal?.aborted || uploadStateRef.current?.aborted) {
                clearMultipartProgress();
            }
            uploadStateRef.current = null;
        }
    }, [initiateMultipartUpload, completeMultipartUpload, abortMultipartUpload, uploadProgress, setUploadProgress]);

    /**
     * Cancel an in-progress multipart upload
     */
    const cancelUpload = useCallback(async () => {
        if (uploadStateRef.current) {
            uploadStateRef.current.aborted = true;

            // Abort all pending XHR requests
            uploadStateRef.current.xhrs.forEach(xhr => {
                try {
                    xhr.abort();
                } catch (e) {
                    // Ignore abort errors
                }
            });

            // Abort the multipart upload on S3
            if (uploadStateRef.current.key && uploadStateRef.current.uploadId) {
                try {
                    await abortMultipartUpload({
                        variables: {
                            data: {
                                key: uploadStateRef.current.key,
                                uploadId: uploadStateRef.current.uploadId
                            }
                        }
                    });
                } catch (error) {
                    console.error('Error aborting multipart upload:', error);
                }
            }
        }
    }, [abortMultipartUpload]);

    /**
     * Check if a file should use multipart upload
     */
    const shouldUseMultipart = (file) => {
        return file.size > MULTIPART_CONFIG.THRESHOLD;
    };

    return {
        uploadMultipart,
        cancelUpload,
        shouldUseMultipart,
        MULTIPART_CONFIG
    };
}

export default useMultipartUpload;
